// ---------------------------------------------------------------------------
// Route-explanation copy — single source of truth, locale-aware.
// ---------------------------------------------------------------------------
// Every user-visible phrase that names a route variant ("Wander",
// "Adventure", "Direct route"), describes a pleasant variant ("Riverside",
// "Forest drive"), or builds a one-sentence summary ("Routes through X and
// Y.") lives here — once per language. Components (RouteCard) and the
// scoring layer (buildExplanation, categorizePleasant) both read from
// this module so phrasing stays in lock-step across languages and across
// the badges that own the time/highway math.
//
// IMPORTANT — separation of concerns:
//   • This module owns COPY only. It never restates ETAs, distances, or
//     highway percentages — those numbers belong to the badges
//     (RouteCard's "+X min" pill, EtaConfidenceChip, HighwayBadge), which
//     compute against the *displayed* baseline. Keeping the prose silent
//     about reference times means the explanation can never contradict
//     the badge it sits next to, regardless of which baseline is active.
//   • Translators only ever change strings in this file. No formatting,
//     thresholds, or selection logic ships in copy — those stay in TS.
//
// No i18n library is used: a flat dictionary keyed by `Locale` is enough
// for the current surface and avoids a runtime dep. `getRouteCopy()` picks
// the active locale from `navigator.language` with English as the fallback,
// so adding a 4th language is one entry in `LOCALES` + one entry in `COPY`.
// ---------------------------------------------------------------------------

export type Locale = "en" | "es" | "fr";

const LOCALES: readonly Locale[] = ["en", "es", "fr"] as const;

/** Human-readable labels for the locale picker UI. */
export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  es: "Español",
  fr: "Français",
};

/** localStorage key for the user-selected locale. Bumped if the shape of
 *  the persisted value ever changes. */
const LOCALE_STORAGE_KEY = "pleasant.locale.v1";

/** Read the persisted locale (set by the in-app switcher) if one exists. */
function getStoredLocale(): Locale | null {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (!raw) return null;
    return (LOCALES as readonly string[]).includes(raw) ? (raw as Locale) : null;
  } catch {
    return null;
  }
}

/** Detect the active UI locale.
 *
 *  Resolution order:
 *    1. Explicit user choice persisted in localStorage (set via `setLocale`).
 *    2. The browser's `navigator.language` (LANGUAGE part only — "es-MX" → "es").
 *    3. English fallback.
 *
 *  SSR-safe. */
export function detectLocale(): Locale {
  const stored = getStoredLocale();
  if (stored) return stored;
  const raw =
    typeof navigator !== "undefined" && typeof navigator.language === "string"
      ? navigator.language.toLowerCase().slice(0, 2)
      : "en";
  return (LOCALES as readonly string[]).includes(raw) ? (raw as Locale) : "en";
}

/** Persist the user's locale choice and notify subscribers. Returns the
 *  saved value so callers can update local state without re-reading. */
export function setLocale(locale: Locale): Locale {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    }
  } catch {
    /* quota / privacy mode — choice will only last this session */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("pleasant:locale-changed", { detail: locale }));
  }
  return locale;
}

/** Clear the persisted locale and fall back to browser detection. */
export function clearStoredLocale(): void {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(LOCALE_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("pleasant:locale-changed", { detail: detectLocale() }));
  }
}

export const SUPPORTED_LOCALES: readonly Locale[] = LOCALES;

// ---------------------------------------------------------------------------
// Copy shape — every locale must implement EVERY field. TypeScript enforces
// this at compile time, so a missing translation is a build error, not a
// runtime "[object Object]" surprise.
// ---------------------------------------------------------------------------

export interface RouteCopy {
  /** Route-classification card titles + subtitles. */
  classification: {
    fastest: { label: string; sub: string };
    pleasant: { label: string; sub: string };
    scenic: { label: string; sub: string };
  };

  /** "Same as fastest" inline note shown on the Direct-route card when the
   *  fastest pick also happens to be the nicest one. */
  sameAsFastest: string;

  /** "vs Direct ETA" badge phrasing — strings rendered by
   *  `<GoogleEtaBadge />`. `same` is shown when the ETAs are within the
   *  noise band; `longer`/`shorter` interpolate the absolute minute delta. */
  googleEta: {
    same: string;
    longer: (minutes: number) => string;
    shorter: (minutes: number) => string;
  };

  /** One-sentence explanations built by `buildExplanation()`. All take
   *  fully-formatted, locale-friendly arguments — no number formatting
   *  happens inside the template. */
  explanation: {
    /** "Runs {miles} mi along {label}." for an epic-highway short-circuit. */
    /** Aug 19 units audit: `dist` is a FULLY-FORMATTED distance string
     *  ("3.4 mi" / "5.5 km") — the caller formats via fmtDistance so the
     *  sentence honours the driver's chosen system; templates must not
     *  append their own unit. */
    epicHighway: (dist: string, label: string) => string;
    /** "Routes through {highlights}." — `highlights` is already joined with
     *  the locale's "and" connector. */
    routesThrough: (highlights: string) => string;
    /** Fallback when no concrete highlights are available. */
    calmerPace: string;
    /** Connector used when joining 2 highlights (e.g. "X and Y"). */
    and: string;
  };

  /** Highlight phrases used inside `explanation.routesThrough`. */
  highlights: {
    parks: (n: number) => string;
    landmarks: (n: number) => string;
    coffeeOne: string;
    coffeeMany: (word: string) => string;
    waterAlongNamed: (names: string) => string;
    waterfrontBlocks: string;
    quietStreets: string;
  };

  /** Pleasant-variant labels & subs (categorizePleasant). */
  variant: {
    epicHighway: { sub: string };
    waterKind: {
      ocean: { label: string; sub: (name: string) => string };
      river: { label: string; sub: (name: string) => string };
      lake: { label: string; sub: (name: string) => string };
      canal: { label: string; sub: (name: string) => string };
      generic: { label: string; sub: (name: string) => string };
    };
    waterNamed: (shortName: string) => string;
    forest: { label: string; sub: string };
    forestNamed: (shortName: string) => string;
    forestNamedSub: (fullName: string) => string;
    parks: { label: string; sub: string };
    parksNamed: (shortName: string) => string;
    parksNamedSub: (fullName: string) => string;
    historic: { label: string; sub: string };
    historicNamed: (shortName: string) => string;
    historicNamedSub: (fullName: string) => string;
    backroads: { label: string; sub: string };
    quickerOption: { label: string; sub: string };
  };

  /** Spelled-out small numbers used in highlight phrases ("two", "three"). */
  numberWord: (n: number) => string;
}

// ---------------------------------------------------------------------------
// English (canonical — all other locales mirror this shape exactly).
// ---------------------------------------------------------------------------

const EN_NUMBERS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];
const ES_NUMBERS = ["cero", "uno", "dos", "tres", "cuatro", "cinco", "seis", "siete", "ocho", "nueve"];
const FR_NUMBERS = ["zéro", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf"];

const en: RouteCopy = {
  classification: {
    fastest:  { label: "Fast route", sub: "The fastest way there" },
    pleasant: { label: "Pleasant route", sub: "Small detours through nicer streets" },
    scenic:   { label: "Scenic route",   sub: "A longer way through the good stuff" },
  },
  sameAsFastest: "The fast route is also the nicest one here",
  googleEta: {
    same: "≈ same time as the fast route",
    longer: (n) => `${n} min longer than the fast route`,
    shorter: (n) => `${n} min shorter than the fast route`,
  },
  explanation: {
    epicHighway: (dist, label) => `Runs ${dist} along ${label}.`,
    routesThrough: (h) => `Routes through ${h}.`,
    calmerPace: "a calmer pace",
    and: " and ",
  },
  highlights: {
    parks: (n) => `${n} park${n > 1 ? "s" : ""}`,
    landmarks: (n) => `${n} landmark${n > 1 ? "s" : ""}`,
    coffeeOne: "a coffee shop you'd like",
    coffeeMany: (word) => `${word} coffee shops you'd like`,
    waterAlongNamed: (names) => `along ${names}`,
    waterfrontBlocks: "waterfront blocks",
    quietStreets: "mostly quiet streets",
  },
  variant: {
    epicHighway: { sub: "Legendary scenic highway" },
    waterKind: {
      ocean:   { label: "Ocean view",   sub: (n) => (n ? `Hugs ${n}` : "Coastal & sea views") },
      river:   { label: "Riverside",    sub: (n) => (n ? `Along the ${n}` : "Follows the river") },
      lake:    { label: "Lakeside",     sub: (n) => (n ? `Along ${n}` : "Past lakes & ponds") },
      canal:   { label: "Canal route",  sub: (n) => (n ? `Along ${n}` : "Follows the canal") },
      generic: { label: "Waterfront",   sub: (n) => (n ? `Along ${n}` : "Water views along the way") },
    },
    waterNamed: (s) => `Along ${s}`,
    forest: { label: "Forest route", sub: "Wooded & green" },
    forestNamed: (s) => `Through ${s}`,
    forestNamedSub: (n) => `Through ${n}`,
    parks: { label: "Parks & gardens", sub: "Greenways & parks" },
    parksNamed: (s) => `Past ${s}`,
    parksNamedSub: (n) => `Past ${n}`,
    historic: { label: "Historic route", sub: "Old streets & landmarks" },
    historicNamed: (s) => `Past ${s}`,
    historicNamedSub: (n) => `Past ${n}`,
    backroads: { label: "Backroads", sub: "Quiet residential streets" },
    quickerOption: { label: "Quicker option", sub: "Shorter than the pleasant route" },
  },
  numberWord: (n) => EN_NUMBERS[n] ?? String(n),
};

// ---------------------------------------------------------------------------
// Spanish.
// ---------------------------------------------------------------------------

const es: RouteCopy = {
  classification: {
    fastest:  { label: "Ruta más rápida", sub: "El camino más directo" },
    pleasant: { label: "Ruta agradable", sub: "Pequeños desvíos por calles más agradables" },
    scenic:   { label: "Aventura",     sub: "Un recorrido más largo por lo mejor" },
  },
  sameAsFastest: "La ruta directa también es la más agradable aquí",
  googleEta: {
    same: "≈ mismo tiempo que Direct",
    longer: (n) => `${n} min más que Direct`,
    shorter: (n) => `${n} min menos que Direct`,
  },
  explanation: {
    epicHighway: (dist, label) => `Recorre ${dist} por ${label}.`,
    routesThrough: (h) => `Pasa por ${h}.`,
    calmerPace: "un ritmo más tranquilo",
    and: " y ",
  },
  highlights: {
    parks: (n) => (n === 1 ? "1 parque" : `${n} parques`),
    landmarks: (n) => (n === 1 ? "1 lugar emblemático" : `${n} lugares emblemáticos`),
    coffeeOne: "una cafetería que te gustaría",
    coffeeMany: (word) => `${word} cafeterías que te gustarían`,
    waterAlongNamed: (names) => `junto a ${names}`,
    waterfrontBlocks: "calles junto al agua",
    quietStreets: "calles mayormente tranquilas",
  },
  variant: {
    epicHighway: { sub: "Carretera escénica legendaria" },
    waterKind: {
      ocean:   { label: "Vista al mar",   sub: (n) => (n ? `Bordea ${n}` : "Vistas al mar y la costa") },
      river:   { label: "Ribera",         sub: (n) => (n ? `Por el ${n}` : "Sigue el río") },
      lake:    { label: "Orilla del lago",sub: (n) => (n ? `Por ${n}` : "Junto a lagos y estanques") },
      canal:   { label: "Por el canal",   sub: (n) => (n ? `Por ${n}` : "Sigue el canal") },
      generic: { label: "Junto al agua",  sub: (n) => (n ? `Por ${n}` : "Vistas al agua en el camino") },
    },
    waterNamed: (s) => `Por ${s}`,
    forest: { label: "Ruta entre árboles", sub: "Verde y arbolada" },
    forestNamed: (s) => `Por ${s}`,
    forestNamedSub: (n) => `A través de ${n}`,
    parks: { label: "Parques y jardines", sub: "Avenidas verdes y parques" },
    parksNamed: (s) => `Por ${s}`,
    parksNamedSub: (n) => `Pasa por ${n}`,
    historic: { label: "Ruta histórica", sub: "Calles antiguas y monumentos" },
    historicNamed: (s) => `Por ${s}`,
    historicNamedSub: (n) => `Pasa por ${n}`,
    backroads: { label: "Calles secundarias", sub: "Calles residenciales tranquilas" },
    quickerOption: { label: "Opción más rápida", sub: "Más corta que Pasea" },
  },
  numberWord: (n) => ES_NUMBERS[n] ?? String(n),
};

// ---------------------------------------------------------------------------
// French.
// ---------------------------------------------------------------------------

const fr: RouteCopy = {
  classification: {
    fastest:  { label: "Itinéraire le plus rapide", sub: "Le chemin le plus direct" },
    pleasant: { label: "Route agréable",    sub: "Petits détours par de plus jolies rues" },
    scenic:   { label: "Aventure",          sub: "Une boucle plus longue par les beaux endroits" },
  },
  sameAsFastest: "L'itinéraire direct est aussi le plus agréable ici",
  googleEta: {
    same: "≈ même durée que Direct",
    longer: (n) => `${n} min de plus que Direct`,
    shorter: (n) => `${n} min de moins que Direct`,
  },
  explanation: {
    epicHighway: (dist, label) => `Parcourt ${dist} le long de ${label}.`,
    routesThrough: (h) => `Passe par ${h}.`,
    calmerPace: "un rythme plus calme",
    and: " et ",
  },
  highlights: {
    parks: (n) => (n === 1 ? "1 parc" : `${n} parcs`),
    landmarks: (n) => (n === 1 ? "1 site remarquable" : `${n} sites remarquables`),
    coffeeOne: "un café qui vous plaira",
    coffeeMany: (word) => `${word} cafés qui vous plairont`,
    waterAlongNamed: (names) => `le long de ${names}`,
    waterfrontBlocks: "rues au bord de l'eau",
    quietStreets: "rues plutôt calmes",
  },
  variant: {
    epicHighway: { sub: "Route panoramique mythique" },
    waterKind: {
      ocean:   { label: "Vue sur mer",   sub: (n) => (n ? `Longe ${n}` : "Vues sur la côte et la mer") },
      river:   { label: "Bord de rivière", sub: (n) => (n ? `Le long de la ${n}` : "Suit la rivière") },
      lake:    { label: "Bord de lac",   sub: (n) => (n ? `Le long de ${n}` : "Près des lacs et étangs") },
      canal:   { label: "Le long du canal", sub: (n) => (n ? `Le long de ${n}` : "Suit le canal") },
      generic: { label: "Au bord de l'eau", sub: (n) => (n ? `Le long de ${n}` : "Vues sur l'eau en chemin") },
    },
    waterNamed: (s) => `Le long de ${s}`,
    forest: { label: "Route en forêt", sub: "Boisée et verdoyante" },
    forestNamed: (s) => `À travers ${s}`,
    forestNamedSub: (n) => `À travers ${n}`,
    parks: { label: "Parcs et jardins", sub: "Voies vertes et parcs" },
    parksNamed: (s) => `Près de ${s}`,
    parksNamedSub: (n) => `Près de ${n}`,
    historic: { label: "Route historique", sub: "Vieilles rues et monuments" },
    historicNamed: (s) => `Près de ${s}`,
    historicNamedSub: (n) => `Près de ${n}`,
    backroads: { label: "Petites routes", sub: "Rues résidentielles calmes" },
    quickerOption: { label: "Option plus rapide", sub: "Plus courte que Flânerie" },
  },
  numberWord: (n) => FR_NUMBERS[n] ?? String(n),
};

const COPY: Record<Locale, RouteCopy> = { en, es, fr };

/** Return the copy bundle for a specific locale (defaults to the detected
 *  active locale). Pass an explicit locale in tests for determinism. */
export function getRouteCopy(locale: Locale = detectLocale()): RouteCopy {
  return COPY[locale] ?? COPY.en;
}
